import clsx from "clsx"
import { format } from "date-fns"
import { FC, RefObject, useState } from "react"
import { btnPresets } from "../../../../../components/button/Button"
import { DetailCollapseButton } from "../../../../../components/button/DetailCollapseButton"
import { GradientFilledButton } from "../../../../../components/button/variants/GradientFilledButton/GradientFilledButton"
import { Card } from "../../../../../components/Card"
import { EmptyState } from "../../../../../components/EmptyState/EmptyState"
import { LoadingIndicator } from "../../../../../components/LoadingIndicator/LoadingIndicator"
import { NavLink } from "../../../../../components/NavLink"
import {
  Pagination,
  PaginationInfo,
} from "../../../../../components/Pagination"
import { Spensor } from "../../../../../components/Spensor"
import { TipIcon } from "../../../../../components/TipIcon/TipIcon"
import { TokenCount } from "../../../../../components/TokenCount"
import { TokenIcon } from "../../../../../components/TokenIcon"
import { TokenName } from "../../../../../components/TokenName"
import { FCC } from "../../../../../utils/reactHelpers/types"
import { withProps } from "../../../../../utils/reactHelpers/withProps"
import { simplifyIdentifier } from "../../../../../utils/stringHelpers"
import {
  readResource,
  SuspenseResource,
} from "../../../../../utils/SuspenseResource"
import { CanScrollMask } from "../../../../Notify/components/CanScrollMask/CanScrollMask"
import {
  WrapBridgeHistoryRecord,
  WrapBridgeSegment,
  WrapBridgeSegmentStatus,
} from "../../../types/types"

export type WrapBridgeHistoryContentProps = {
  headerRef?: RefObject<HTMLDivElement>
  records: SuspenseResource<WrapBridgeHistoryRecord[]>
  pagination: SuspenseResource<PaginationInfo>
  onChangePage: (page: number) => void
}

const Header = withProps(
  "div",
  {
    className: "text-xs leading-4 font-medium text-gray-200 opacity-50 mb",
  },
  { className: clsx },
)

const SegmentContainer: FCC<{
  title: string
}> = props => (
  <div className="flex flex-col flex-[200]">
    <Header>{props.title}</Header>
    {props.children}
  </div>
)

const SegmentComponent: FC<{
  segment: WrapBridgeSegment
}> = props => (
  <>
    <div className="text-base leading-6 text-gray-200 mt-1">
      {props.segment.amount != null ? (
        <TokenCount token={props.segment.token} count={props.segment.amount} />
      ) : (
        <>
          ~
          <TokenCount
            token={props.segment.token}
            count={props.segment.estimatedAmount}
          />
        </>
      )}{" "}
      <TokenName token={props.segment.token} />
    </div>
    <span className="text-sm leading-5">
      <NavLink to={props.segment.addressExploreLink} className="text-blue-600">
        {simplifyIdentifier(props.segment.address)}
      </NavLink>{" "}
      ({props.segment.network.name})
    </span>
  </>
)

const FeeComponent: FC<{ row: WrapBridgeHistoryRecord }> = props => (
  <div className="flex flex-col flex-[150]">
    <Header className="flex items-center gap-2">
      Fee
      <TipIcon tip="TBD" />
    </Header>
    <div className="text-base leading-6 text-gray-200 mt-1">
      {!WrapBridgeHistoryRecord.isSignMissing(props.row) ? (
        <>
          <TokenCount token={props.row.feeToken} count={props.row.fee} />{" "}
          <TokenName token={props.row.feeToken} />
        </>
      ) : (
        "-"
      )}
    </div>
  </div>
)

const SegmentStatusBadge: FC<{ status?: WrapBridgeSegmentStatus }> = props => {
  if (props.status == null) {
    return null
  }
  return (
    <div className="flex flex-row items-center gap-1 text-xs">
      {props.status.type === "success" ? (
        <svg width="12" height="12" viewBox="0 0 12 12">
          <path
            d="M4.78846 10.3269L0.75 6.04696L1.87996 4.89237L4.76706 7.91031L10.0752 2.25L11.25 3.38319L4.78846 10.3269Z"
            fill="#22C55E"
          />
        </svg>
      ) : props.status.type === "inProgress" ? (
        <svg width="12" height="12" viewBox="0 0 12 12">
          <path
            d="M6 1C8.757 1 11 3.243 11 6C11 8.757 8.757 11 6 11C3.243 11 1 8.757 1 6C1 3.243 3.243 1 6 1ZM6 0C2.6865 0 0 2.6865 0 6C0 9.3135 2.6865 12 6 12C9.3135 12 12 9.3135 12 6C12 2.6865 9.3135 0 6 0ZM6.5 6V3H5.5V7H9V6H6.5Z"
            fill="#C4C4C4"
          />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12">
          <path
            d="M6.99995 1.40313C6.99995 1.45313 7.09995 1.55313 7.24995 1.80313C7.39995 2.05313 7.54995 2.30313 7.74995 2.70313C7.94995 3.10313 8.19995 3.45313 8.44995 3.90313C8.69995 4.35313 8.99995 4.80313 9.24995 5.25313C9.49995 5.70313 9.79995 6.20313 10.05 6.65313C10.3 7.10313 10.55 7.55313 10.8 7.90313C11 8.30313 11.2 8.60313 11.4 8.90313C11.6 9.20313 11.65 9.35313 11.75 9.45313C11.85 9.65313 11.95 9.85313 11.95 10.0531C12 10.2531 11.95 10.4031 11.9 10.6031C11.85 10.8031 11.75 10.9031 11.65 11.0031C11.5 11.1031 11.35 11.1531 11.15 11.1531H1.04995C0.749951 11.1531 0.499951 11.1031 0.349951 11.0031C0.199951 10.9031 0.0999512 10.7531 0.0499512 10.6031C-4.88284e-05 10.4531 -4.88284e-05 10.2531 0.0499512 10.1031C0.0999512 9.90313 0.149951 9.75313 0.299951 9.55313C0.349951 9.45313 0.449951 9.30313 0.599951 9.00313C0.749951 8.75313 0.949951 8.40313 1.19995 8.05313C1.44995 7.65313 1.69995 7.25313 1.94995 6.80313C2.19995 6.35313 2.49995 5.85313 2.79995 5.40313C3.09995 4.95313 3.34995 4.45313 3.59995 4.00313C3.84995 3.55313 4.09995 3.15313 4.29995 2.80313C4.49995 2.45313 4.69995 2.15313 4.84995 1.90313L5.09995 1.50313C5.19995 1.35313 5.34995 1.20313 5.54995 1.10313C5.74995 1.00313 5.89995 0.953125 6.09995 0.953125C6.29995 0.953125 6.44995 1.00313 6.64995 1.05313C6.74995 1.10313 6.89995 1.25313 6.99995 1.40313ZM6.69995 3.70313C6.69995 3.60313 6.69995 3.50313 6.64995 3.45313C6.59995 3.35313 6.54995 3.30313 6.49995 3.25313C6.44995 3.20313 6.34995 3.15313 6.24995 3.10313C6.14995 3.05313 6.04995 3.00313 5.94995 3.00313C5.74995 3.00313 5.59995 3.05313 5.44995 3.20313C5.29995 3.35313 5.19995 3.50313 5.19995 3.70313V6.80313C5.19995 7.00313 5.29995 7.15313 5.44995 7.30313C5.59995 7.45313 5.74995 7.50313 5.94995 7.50313C6.14995 7.50313 6.29995 7.45313 6.44995 7.30313C6.59995 7.15313 6.69995 7.00313 6.69995 6.80313V3.70313ZM5.94995 8.20313C5.74995 8.20313 5.54995 8.25313 5.44995 8.40313C5.34995 8.55313 5.24995 8.70313 5.24995 8.90313C5.24995 9.10313 5.29995 9.30313 5.44995 9.40313C5.59995 9.55313 5.74995 9.60313 5.94995 9.60313C6.14995 9.60313 6.34995 9.55313 6.44995 9.40313C6.59995 9.25313 6.64995 9.10313 6.64995 8.90313C6.64995 8.70313 6.59995 8.50313 6.44995 8.40313C6.34995 8.25313 6.14995 8.20313 5.94995 8.20313Z"
            fill="#ECD71F"
          />
          <path
            d="M6.70007 3.7C6.70007 3.6 6.70007 3.5 6.65007 3.45C6.60007 3.35 6.55007 3.3 6.50007 3.25C6.45007 3.2 6.35007 3.15 6.25007 3.1C6.15007 3.05 6.05007 3 5.95007 3C5.75007 3 5.60007 3.05 5.45007 3.2C5.30007 3.35 5.20007 3.5 5.20007 3.7V6.8C5.20007 7 5.30007 7.15 5.45007 7.3C5.60007 7.45 5.75007 7.5 5.95007 7.5C6.15007 7.5 6.30007 7.45 6.45007 7.3C6.60007 7.15 6.70007 7 6.70007 6.8V3.7ZM5.95007 8.2C5.75007 8.2 5.55007 8.25 5.45007 8.4C5.35007 8.55 5.25007 8.7 5.25007 8.9C5.25007 9.1 5.30007 9.3 5.45007 9.4C5.60007 9.55 5.75007 9.6 5.95007 9.6C6.15007 9.6 6.35007 9.55 6.45007 9.4C6.60007 9.25 6.65007 9.1 6.65007 8.9C6.65007 8.7 6.60007 8.5 6.45007 8.4C6.35007 8.25 6.15007 8.2 5.95007 8.2Z"
            fill="black"
          />
        </svg>
      )}
      <span
        className={clsx(
          "truncate",
          props.status.type === "success"
            ? "text-green-500"
            : props.status.type === "inProgress"
            ? "text-orange-500"
            : "text-yellow-300",
        )}
      >
        {props.status.type === "success" ? "Succeeded" : props.status.message}
      </span>
      {props.status.explorerLink && (
        <NavLink to={props.status.explorerLink} target="_blank">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.5 6.5V11.5H0V2H6V3H1V10.5H9.5V6.5H10.5ZM12 0.5H6.506L8.5235 2.5L5.035 6.035L6.449 7.449L9.9375 3.914L12 6V0.5Z"
              fill="#4B5563"
            />
          </svg>
        </NavLink>
      )}
    </div>
  )
}

const Arrow: FC = () => (
  <svg width="16" height="25" viewBox="0 0 16 25" className="mt-1">
    <path
      d="M16 12.5L11 9.61325L11 15.3868L16 12.5ZM-4.37114e-08 13L11.5 13L11.5 12L4.37114e-08 12L-4.37114e-08 13Z"
      fill="white"
      fillOpacity={0.2}
    />
  </svg>
)

const StatusComponent: FC<{ row: WrapBridgeHistoryRecord }> = props => (
  <div className="flex flex-col flex-[350]">
    <Header>Status</Header>
    <div className="flex flex-row gap-2">
      <div className="flex flex-col">
        <div className="text-base leading-6 text-gray-200 mt-1 flex items-center gap-1">
          <TokenIcon size={16} token={props.row.source.token} />
          <TokenName token={props.row.source.token} /> sent
        </div>
        <SegmentStatusBadge status={props.row.status.source} />
      </div>
      <Arrow />
      <div className="flex flex-col">
        <div className="text-base leading-6 text-gray-200 mt-1">Wrapping</div>
        <SegmentStatusBadge status={props.row.status.wrapping} />
      </div>
      <Arrow />
      <div className="flex flex-col">
        {WrapBridgeHistoryRecord.isSignMissing(props.row) ? (
          "-"
        ) : (
          <>
            <div className="text-base leading-6 text-gray-200 mt-1 flex items-center gap-1">
              <TokenIcon size={16} token={props.row.target.token} />
              <TokenName token={props.row.target.token} /> sent
            </div>
            <SegmentStatusBadge status={props.row.status.destination} />
          </>
        )}
      </div>
    </div>
  </div>
)

export const WrapBridgeHistoryRow: FC<{
  row: WrapBridgeHistoryRecord
}> = props => {
  return (
    <div className="flex flex-col gap-3 bg-black bg-opacity-20 px-6 py-4 rounded-lg">
      <div className="flex flex-row justify-between items-baseline">
        <div
          className={clsx(
            "flex flex-row items-center gap-2 text-lg leading-7 font-semibold",
            WrapBridgeHistoryRecord.isSignMissing(props.row)
              ? "text-yellow-300"
              : "text-gray-200",
          )}
        >
          {WrapBridgeHistoryRecord.isSignMissing(props.row) && (
            <svg width="18" height="18" viewBox="0 0 12 12">
              <path
                d="M6.99995 1.40313C6.99995 1.45313 7.09995 1.55313 7.24995 1.80313C7.39995 2.05313 7.54995 2.30313 7.74995 2.70313C7.94995 3.10313 8.19995 3.45313 8.44995 3.90313C8.69995 4.35313 8.99995 4.80313 9.24995 5.25313C9.49995 5.70313 9.79995 6.20313 10.05 6.65313C10.3 7.10313 10.55 7.55313 10.8 7.90313C11 8.30313 11.2 8.60313 11.4 8.90313C11.6 9.20313 11.65 9.35313 11.75 9.45313C11.85 9.65313 11.95 9.85313 11.95 10.0531C12 10.2531 11.95 10.4031 11.9 10.6031C11.85 10.8031 11.75 10.9031 11.65 11.0031C11.5 11.1031 11.35 11.1531 11.15 11.1531H1.04995C0.749951 11.1531 0.499951 11.1031 0.349951 11.0031C0.199951 10.9031 0.0999512 10.7531 0.0499512 10.6031C-4.88284e-05 10.4531 -4.88284e-05 10.2531 0.0499512 10.1031C0.0999512 9.90313 0.149951 9.75313 0.299951 9.55313C0.349951 9.45313 0.449951 9.30313 0.599951 9.00313C0.749951 8.75313 0.949951 8.40313 1.19995 8.05313C1.44995 7.65313 1.69995 7.25313 1.94995 6.80313C2.19995 6.35313 2.49995 5.85313 2.79995 5.40313C3.09995 4.95313 3.34995 4.45313 3.59995 4.00313C3.84995 3.55313 4.09995 3.15313 4.29995 2.80313C4.49995 2.45313 4.69995 2.15313 4.84995 1.90313L5.09995 1.50313C5.19995 1.35313 5.34995 1.20313 5.54995 1.10313C5.74995 1.00313 5.89995 0.953125 6.09995 0.953125C6.29995 0.953125 6.44995 1.00313 6.64995 1.05313C6.74995 1.10313 6.89995 1.25313 6.99995 1.40313ZM6.69995 3.70313C6.69995 3.60313 6.69995 3.50313 6.64995 3.45313C6.59995 3.35313 6.54995 3.30313 6.49995 3.25313C6.44995 3.20313 6.34995 3.15313 6.24995 3.10313C6.14995 3.05313 6.04995 3.00313 5.94995 3.00313C5.74995 3.00313 5.59995 3.05313 5.44995 3.20313C5.29995 3.35313 5.19995 3.50313 5.19995 3.70313V6.80313C5.19995 7.00313 5.29995 7.15313 5.44995 7.30313C5.59995 7.45313 5.74995 7.50313 5.94995 7.50313C6.14995 7.50313 6.29995 7.45313 6.44995 7.30313C6.59995 7.15313 6.69995 7.00313 6.69995 6.80313V3.70313ZM5.94995 8.20313C5.74995 8.20313 5.54995 8.25313 5.44995 8.40313C5.34995 8.55313 5.24995 8.70313 5.24995 8.90313C5.24995 9.10313 5.29995 9.30313 5.44995 9.40313C5.59995 9.55313 5.74995 9.60313 5.94995 9.60313C6.14995 9.60313 6.34995 9.55313 6.44995 9.40313C6.59995 9.25313 6.64995 9.10313 6.64995 8.90313C6.64995 8.70313 6.59995 8.50313 6.44995 8.40313C6.34995 8.25313 6.14995 8.20313 5.94995 8.20313Z"
                fill="#ECD71F"
              />
              <path
                d="M6.70007 3.7C6.70007 3.6 6.70007 3.5 6.65007 3.45C6.60007 3.35 6.55007 3.3 6.50007 3.25C6.45007 3.2 6.35007 3.15 6.25007 3.1C6.15007 3.05 6.05007 3 5.95007 3C5.75007 3 5.60007 3.05 5.45007 3.2C5.30007 3.35 5.20007 3.5 5.20007 3.7V6.8C5.20007 7 5.30007 7.15 5.45007 7.3C5.60007 7.45 5.75007 7.5 5.95007 7.5C6.15007 7.5 6.30007 7.45 6.45007 7.3C6.60007 7.15 6.70007 7 6.70007 6.8V3.7ZM5.95007 8.2C5.75007 8.2 5.55007 8.25 5.45007 8.4C5.35007 8.55 5.25007 8.7 5.25007 8.9C5.25007 9.1 5.30007 9.3 5.45007 9.4C5.60007 9.55 5.75007 9.6 5.95007 9.6C6.15007 9.6 6.35007 9.55 6.45007 9.4C6.60007 9.25 6.65007 9.1 6.65007 8.9C6.65007 8.7 6.60007 8.5 6.45007 8.4C6.35007 8.25 6.15007 8.2 5.95007 8.2Z"
                fill="black"
              />
            </svg>
          )}
          Order: {simplifyIdentifier(props.row.orderNumber)}
          {WrapBridgeHistoryRecord.isSignMissing(props.row) && (
            <GradientFilledButton
              className="flex items-center gap-1 ml-2"
              roundedClassName="rounded"
              {...btnPresets.small}
              onClick={props.row.onSignMessage}
            >
              Sign Message
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.2903 0.491607L7.33873 0.432902C7.54659 0.210426 7.87661 0.188214 8.10886 0.381067L8.16371 0.432902L11.0791 3.55425C11.287 3.77675 11.3077 4.13 11.1276 4.37861L11.0791 4.43669L8.16429 7.56179C7.94733 7.79963 7.59434 7.81386 7.36128 7.59418C7.12821 7.37449 7.09676 6.99788 7.28972 6.73742L7.33815 6.67872L9.25706 4.62155H1.33343C1.03765 4.62151 0.788691 4.38454 0.754084 4.0701L0.75 3.99703C0.75 3.68042 0.971418 3.41393 1.26517 3.37689L1.33343 3.37251H9.25881L7.33873 1.31597C7.13089 1.09348 7.11014 0.740219 7.2903 0.491607L7.33873 0.432902L7.2903 0.491607Z"
                  fill="white"
                />
              </svg>
            </GradientFilledButton>
          )}
        </div>
        <div className="text-sm leading-5 text-gray-500">
          Time: {format(props.row.createdAt, "MMM d, yyyy h:mm a")}
        </div>
      </div>
      <div className="flex flex-row justify-between gap-2">
        <SegmentContainer title="Source">
          <SegmentComponent segment={props.row.source} />
        </SegmentContainer>
        <SegmentContainer title="Target">
          {WrapBridgeHistoryRecord.isSignMissing(props.row) ? (
            "-"
          ) : (
            <SegmentComponent segment={props.row.target} />
          )}
        </SegmentContainer>
        <FeeComponent row={props.row} />
        <StatusComponent row={props.row} />
      </div>
    </div>
  )
}

export const WrapBridgeHistoryContent: FC<
  WrapBridgeHistoryContentProps
> = props => {
  const [show, setShow] = useState(true)
  return (
    <CanScrollMask>
      <Card className="flex flex-col gap-2 min-w-min">
        <div
          className="flex flex-row items-center justify-between"
          ref={props.headerRef}
        >
          <h2 className="text-xl leading-7 font-medium">Order History</h2>
          <DetailCollapseButton show={show} onClick={() => setShow(!show)} />
        </div>
        {show && (
          <>
            <Spensor fallback={<LoadingIndicator className="mx-auto" />}>
              {() => {
                const records = readResource(props.records)
                if (records.length === 0) {
                  return (
                    <EmptyState className={"mx-auto my-20"}>
                      Your history orders will appear here.
                    </EmptyState>
                  )
                }
                return (
                  <ul className="flex flex-col gap-2">
                    {records.map(value => (
                      <WrapBridgeHistoryRow
                        row={value}
                        key={value.orderNumber}
                      />
                    ))}
                  </ul>
                )
              }}
            </Spensor>
            <Spensor>
              {() =>
                props.pagination != null ? (
                  <Pagination
                    {...readResource(props.pagination)}
                    onChange={i => props.onChangePage?.(i.page)}
                  />
                ) : null
              }
            </Spensor>
          </>
        )}
      </Card>
    </CanScrollMask>
  )
}
